import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, X, Check, Trash2, Image as ImageIcon } from "lucide-react";
import MediaUploader from "./MediaUploader";
import { resolveMediaUrl } from "../lib/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (_) {
    return "";
  }
};

const formatBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * MediaLibrary — full panel + modal selector.
 *
 * Props:
 *  - token       (admin token)
 *  - mode        "panel" | "picker"
 *  - onSelect(media)    callback when a row is chosen (picker mode)
 *  - onClose()          close handler (picker mode)
 *  - selectedId  highlight current selection
 */
const MediaLibrary = ({
  token,
  mode = "panel",
  onSelect,
  onClose,
  selectedId,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // jpg | png | webp | all
  const [sort, setSort] = useState("newest");

  const reload = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (filter !== "all") params.set("ext", filter);
      params.set("sort", sort);
      const res = await axios.get(`${API}/admin/media?${params.toString()}`, {
        headers: { "x-admin-token": token },
      });
      setItems(res.data || []);
    } catch (err) {
      toast.error("Failed to load media library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, [filter, sort]);

  // Live search (client-side filter over loaded list)
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (m) =>
        (m.original_name || "").toLowerCase().includes(q) ||
        (m.filename || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const removeOne = async (m) => {
    if (!window.confirm(`Delete "${m.original_name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/media/${m.id}`, {
        headers: { "x-admin-token": token },
      });
      toast.success("Image deleted");
      setItems((prev) => prev.filter((x) => x.id !== m.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const onUploaded = (added) => {
    const list = Array.isArray(added) ? added : [added];
    setItems((prev) => [...list, ...prev]);
  };

  // ---------- Panel / picker chrome ----------
  const inner = (
    <div
      data-testid="media-library"
      className={`flex flex-col gap-4 ${mode === "picker" ? "p-5 md:p-7" : ""}`}
    >
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
            Media Library · {items.length} image{items.length === 1 ? "" : "s"}
          </div>
          <div className="mt-1 text-xs text-[#8b8b8f]">
            {mode === "picker"
              ? "Pick a cover image, or upload a new one."
              : "Manage all images used across your blog."}
          </div>
        </div>
        {mode === "picker" && onClose && (
          <button
            onClick={onClose}
            data-testid="media-picker-close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white text-[#1c1c1e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
            aria-label="Close media picker"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Uploader */}
      <MediaUploader token={token} multiple onUploaded={onUploaded} />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search
            size={12}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8b8b8f]"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search images by name…"
            data-testid="media-search"
            className="w-full rounded-full border border-black/10 bg-white py-1.5 pl-8 pr-3 text-sm focus:border-[#e85a1c] focus:outline-none"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="media-filter"
          className="rounded-full border border-black/10 bg-white px-3 py-1.5 font-mono-industrial text-[10px] uppercase tracking-[0.18em] focus:border-[#e85a1c] focus:outline-none"
        >
          <option value="all">All types</option>
          <option value="jpg">JPG</option>
          <option value="png">PNG</option>
          <option value="webp">WEBP</option>
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="media-sort"
          className="rounded-full border border-black/10 bg-white px-3 py-1.5 font-mono-industrial text-[10px] uppercase tracking-[0.18em] focus:border-[#e85a1c] focus:outline-none"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
        </select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="py-10 text-center font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
          Loading…
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-black/10 bg-white py-10 text-center">
          <ImageIcon size={28} className="mx-auto mb-2 text-[#bababd]" />
          <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
            {search ? "No matches" : "No images yet — upload one above."}
          </div>
        </div>
      ) : (
        <div
          data-testid="media-grid"
          className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 max-h-[55vh] overflow-y-auto pr-1"
        >
          {visible.map((m) => {
            const thumbUrl = (m.urls?.thumb || m.urls?.medium || m.url || "");
            const absolute = resolveMediaUrl(thumbUrl);
            const isSelected = selectedId === m.id;
            return (
              <div
                key={m.id}
                data-testid={`media-card-${m.id}`}
                className={`group relative overflow-hidden rounded-xl border bg-white transition-all hover:-translate-y-0.5 hover:shadow-md ${
                  isSelected ? "border-[#e85a1c] ring-2 ring-[#e85a1c]/30" : "border-black/10"
                }`}
              >
                <button
                  type="button"
                  onClick={() => mode === "picker" && onSelect?.(m)}
                  className="block w-full"
                  data-testid={`media-pick-${m.id}`}
                  disabled={mode !== "picker"}
                >
                  <img
                    src={absolute}
                    alt={m.original_name}
                    loading="lazy"
                    className="block aspect-[16/9] w-full object-cover"
                  />
                </button>
                <div className="flex items-center justify-between gap-2 px-2 py-1.5">
                  <div className="min-w-0 flex-1">
                    <div
                      className="truncate text-[11px] text-[#1c1c1e]"
                      title={m.original_name}
                    >
                      {m.original_name}
                    </div>
                    <div className="font-mono-industrial text-[8.5px] uppercase tracking-[0.18em] text-[#8b8b8f]">
                      {m.ext} · {formatBytes(m.size_bytes)} · {formatDate(m.created_at)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeOne(m)}
                    title="Delete"
                    data-testid={`media-delete-${m.id}`}
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-black/10 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={10} />
                  </button>
                </div>
                {isSelected && (
                  <div className="absolute right-1.5 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#e85a1c] text-white">
                    <Check size={11} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {mode === "picker" && (
        <div className="flex items-center justify-end gap-2 border-t border-black/5 pt-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="media-picker-cancel"
            className="rounded-full border border-black/10 px-4 py-1.5 font-mono-industrial text-[10px] uppercase tracking-[0.2em] text-[#4a4a4e] hover:border-[#1c1c1e] hover:text-[#1c1c1e]"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );

  // Modal wrap for picker
  if (mode === "picker") {
    return (
      <div
        className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/60 backdrop-blur-sm p-4 md:p-8"
        data-testid="media-picker-modal"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        <div className="w-full max-w-4xl rounded-3xl bg-[#f7f7f5] shadow-2xl">
          {inner}
        </div>
      </div>
    );
  }

  return inner;
};

export default MediaLibrary;
